import { DEFAULT_SHOP_LOCALE, type ShopLocale, normalizeShopLocale } from '../../i18n/shopLocale'
import { normalizePathname } from '../routing'
import type { SeoLocaleAlternate, SeoLocaleFallback } from './types'

const SUPPORTED_LOCALES: ShopLocale[] = ['it-IT']

const toCanonicalPath = (slug: string) => {
  const normalizedSlug = String(slug || '').trim()
  if (!normalizedSlug || normalizedSlug === '/') return '/'
  return normalizePathname(`/${normalizedSlug.replace(/^\/+/, '')}`)
}

export const buildCanonicalPathFromCmsSlug = (slug: string) => toCanonicalPath(slug)

export const buildDefaultSeoAlternates = (canonicalPath: string): SeoLocaleAlternate[] =>
  SUPPORTED_LOCALES.map((locale) => ({
    locale,
    path: normalizePathname(canonicalPath),
  }))

export const dedupeSeoAlternates = (
  alternates: SeoLocaleAlternate[],
  fallbackPath: string,
): SeoLocaleAlternate[] => {
  const resolved = new Map<ShopLocale, string>()
  for (const entry of alternates) {
    if (!entry || !entry.locale) continue
    const path = entry.path?.trim() ? normalizePathname(entry.path) : normalizePathname(fallbackPath)
    resolved.set(entry.locale, path)
  }
  return Array.from(resolved.entries()).map(([locale, path]) => ({ locale, path }))
}

export const buildSeoAlternatesFromRawTranslations = (
  translations: unknown,
  pathResolver: (slug: string) => string = buildCanonicalPathFromCmsSlug,
): SeoLocaleAlternate[] => {
  if (!Array.isArray(translations)) return []
  const alternates: SeoLocaleAlternate[] = []
  for (const entry of translations) {
    if (!entry || typeof entry !== 'object') continue
    const raw = entry as Record<string, unknown>
    const locale = normalizeShopLocale(raw.locale)
    if (!locale) continue
    const status = raw.status === 'published' ? 'published' : 'draft'
    const translationStatus = raw.translationStatus === 'published' ? 'published' : 'draft'
    if (status !== 'published' && translationStatus !== 'published') continue
    const slug = typeof raw.slug === 'string' ? raw.slug : ''
    alternates.push({
      locale,
      path: pathResolver(slug),
    })
  }
  return alternates
}

export const shouldFallbackToDefaultLocale = ({
  requestedLocale,
  responseStatus,
}: {
  requestedLocale: ShopLocale
  responseStatus: number
}) => responseStatus === 404 && requestedLocale !== DEFAULT_SHOP_LOCALE

export const buildLocaleFallbackSignal = ({
  requestedLocale,
  resolvedLocale,
}: {
  requestedLocale: ShopLocale
  resolvedLocale: ShopLocale
}): SeoLocaleFallback | null =>
  requestedLocale === resolvedLocale
    ? null
    : {
        policy: 'default-locale-content',
        requestedLocale,
        resolvedLocale,
      }
